import React from 'react';
import { Link } from 'react-router-dom';
import { FireIcon } from '@heroicons/react/24/outline';
import { PlayerWinStreakPayload } from '../../../generated-api';

interface PlayerWinStreakCardProps {
  payload: PlayerWinStreakPayload;
  createdAt: string;
}

export const PlayerWinStreakCard: React.FC<PlayerWinStreakCardProps> = ({ payload, createdAt }) => {
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStreakMessage = (streak: number) => {
    switch (streak) {
      case 3: return 'is on a hot streak!';
      case 5: return 'is unstoppable!';
      case 10: return 'is a legend!';
      default: return 'is dominating!';
    }
  };

  return (
    <Link
      data-track="feed_win_streak_card"
      to={`/players/${payload.playerId}`}
      className="block bg-surface rounded-lg shadow-sm border border-edge hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 bg-orange-100 rounded-full p-2">
              <FireIcon className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-heading">Win Streak!</p>
              <p className="text-xs text-muted">{formatRelativeTime(createdAt)}</p>
            </div>
          </div>
          {payload.playerAvatar ? (
            <img
              src={payload.playerAvatar}
              alt={payload.playerName}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-surface-skeleton flex items-center justify-center text-lg font-medium text-body">
              {payload.playerName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="mt-3">
          <p className="text-sm text-body">
            <span className="font-medium text-orange-600">{payload.playerName}</span>
            {' won '}
            <span className="font-bold text-orange-600">{payload.streakCount} games in a row</span>
            {' and '}
            {getStreakMessage(payload.streakCount)}
          </p>
        </div>
      </div>
    </Link>
  );
};
